/**
 * Pricing configuration — SINGLE SOURCE OF TRUTH for all price calculations.
 * Extracted from validationLimits.ts to separate pricing concerns from validation.
 */

import {
  normalizePageCount,
  type PageCountBookType,
} from './validationLimits.js';

export interface PricingTier {
  /** Base price in cents */
  basePriceCents: number;
  /** Number of pages included in the base price */
  standardPageCount: number;
}

/**
 * Pricing configuration — SINGLE SOURCE OF TRUTH for all price calculations.
 * Prices are in US cents to avoid floating-point issues (Stripe convention).
 */
export const PRICING_CONFIG = {
  digital_only: { basePriceCents: 2900, standardPageCount: 16 },
  softcover: { basePriceCents: 4900, standardPageCount: 16 },
  hardcover: { basePriceCents: 6900, standardPageCount: 24 },
  /** Cost per additional page beyond the standard count, in cents */
  extraPageCostCents: 50,
} as const;

/**
 * Return the number of pages included in the base price for a purchase type.
 */
export function getStandardPageCount(purchaseType: PageCountBookType): number {
  const tier: PricingTier | undefined = PRICING_CONFIG[
    purchaseType as keyof typeof PRICING_CONFIG
  ] as PricingTier | undefined;
  if (!tier) {
    return PRICING_CONFIG.digital_only.standardPageCount;
  }
  return tier.standardPageCount;
}

/**
 * Return the base price (in cents) for a purchase type.
 * Useful for "starting at $XX" display.
 */
export function getBasePrice(purchaseType: PageCountBookType): number {
  const tier: PricingTier | undefined = PRICING_CONFIG[
    purchaseType as keyof typeof PRICING_CONFIG
  ] as PricingTier | undefined;
  if (!tier) {
    return PRICING_CONFIG.digital_only.basePriceCents;
  }
  return tier.basePriceCents;
}

/**
 * Calculate the total price in cents for a given purchase type and page count.
 * Extra pages beyond the standard count are charged at `extraPageCostCents` each.
 * Fewer pages than the standard count do NOT reduce the price below the base.
 */
export function calculatePrice(
  purchaseType: PageCountBookType,
  pageCount: number
): number {
  const base = getBasePrice(purchaseType);
  const standard = getStandardPageCount(purchaseType);
  const clamped = normalizePageCount(purchaseType, pageCount);
  const extraPages = Math.max(0, clamped - standard);
  return base + extraPages * PRICING_CONFIG.extraPageCostCents;
}

/**
 * Format a price in cents as a display string.
 * Whole-dollar amounts render as "$XX", half-dollar amounts as "$XX.50".
 * Arbitrary cent values render with two decimal places.
 */
export function formatPrice(cents: number): string {
  const dollars = Math.floor(cents / 100);
  const remainder = cents % 100;
  if (remainder === 0) {
    return `$${dollars}`;
  }
  return `$${dollars}.${String(remainder).padStart(2, '0')}`;
}

/**
 * Return a human-readable explanation of the pricing model for a purchase type.
 */
export function getPricingNote(purchaseType?: PageCountBookType): string {
  const extraCost = formatPrice(PRICING_CONFIG.extraPageCostCents);
  if (purchaseType) {
    const standard = getStandardPageCount(purchaseType);
    return `Base price includes ${standard} pages. Each additional page is ${extraCost}.`;
  }
  return `Each additional page beyond the included amount is ${extraCost}.`;
}
